import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type AppNotification = {
  id: string;
  title: string;
  body: string;
  createdAt: number;
  isRead: boolean;
  source: 'push' | 'local';
  data?: Record<string, any>;
};

export type NotificationsSliceObject = {
  items: AppNotification[];
};

const initialState: NotificationsSliceObject = {
  items: [],
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (
      state,
      action: PayloadAction<{
        id?: string;
        title?: string;
        body?: string;
        source?: 'push' | 'local';
        createdAt?: number;
        data?: Record<string, any>;
      }>,
    ) => {
      const payload = action.payload || {};
      const createdAt = payload.createdAt || Date.now();
      const id =
        payload.id || `${createdAt}-${Math.random().toString(36).slice(2, 10)}`;
      state.items.unshift({
        id,
        title: payload.title?.trim() || 'Notification',
        body: payload.body?.trim() || '',
        createdAt,
        isRead: false,
        source: payload.source || 'local',
        data: payload.data,
      });
    },
    markNotificationRead: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const target = state.items.find(item => item.id === id);
      if (target) target.isRead = true;
    },
    setNotificationReadStatus: (
      state,
      action: PayloadAction<{ id: string; isRead: boolean }>,
    ) => {
      const { id, isRead } = action.payload;
      const target = state.items.find(item => item.id === id);
      if (target) target.isRead = isRead;
    },
    markAllNotificationsRead: state => {
      state.items = state.items.map(item => ({ ...item, isRead: true }));
    },
    deleteNotification: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.id !== action.payload);
    },
    clearNotifications: state => {
      state.items = [];
    },
    clearReadNotifications: state => {
      state.items = state.items.filter(item => !item.isRead);
    },
  },
});

export const {
  addNotification,
  markNotificationRead,
  setNotificationReadStatus,
  markAllNotificationsRead,
  deleteNotification,
  clearNotifications,
  clearReadNotifications,
} = notificationsSlice.actions;

export const selectNotifications = (state: {
  notifications: NotificationsSliceObject;
}) => state.notifications.items;

export const selectUnreadNotificationsCount = (state: {
  notifications: NotificationsSliceObject;
}) => state.notifications.items.filter(item => !item.isRead).length;

export default notificationsSlice.reducer;
